// @tennis-matches/clubmeisterschaft-core/schedule – Groups RR + KO bracket builder (pure, no DB)

import {
  generateCode,
  __asStr,
  orderPlayersForDraw,
  stableInterleaveShuffle,
} from "@tennis-matches/core/utils";

/**
 * Build canonical schedule from players + groupsKo config.
 * @param {object} input
 * @param {Array<{pid:string,name:string}>} input.players
 * @param {object} input.groupsKo
 * @returns {object} schedule
 */
export function buildGroupsKoSchedule({ players, groupsKo }) {
  const p = Array.isArray(players) ? players : [];
  const gk = groupsKo && typeof groupsKo === "object" ? groupsKo : {};

  const groupCount = Math.max(2, Number(gk.groups?.groupCount) || 4);
  const seedingMode = __asStr(gk.groups?.seedingMode || "random");
  const allowUneven = gk.groups?.allowUnevenGroups !== false;

  const seedCount = Math.max(0, Number(gk.groups?.seedCount) || 0);

  const advancePerGroup = Math.max(
    1,
    Number(gk.qualification?.advancePerGroup) || 2,
  );
  const includeBestThirds = Math.max(
    0,
    Number(gk.qualification?.includeBestThirds) || 0,
  );

  const bracketSize = Number(gk.ko?.bracketSize) || 8;
  const thirdPlaceMatch = !!gk.ko?.thirdPlaceMatch;

  // 1) Reihenfolge: LK-Setzliste oder Zufall
  const ordered = orderPlayersForDraw(
    p,
    { seedingMode, seedCount },
    stableInterleaveShuffle,
  );

  // 2) distribute into groups: snake draft for fairness
  const groups = makeGroups(ordered, groupCount, allowUneven);

  // 3) round-robin matches per group (circle method)
  const groupMatches = [];
  for (const group of groups) {
    const ids = group.playerIds.slice();
    const rr = buildRoundRobin(ids);
    rr.forEach((roundPairs, roundIndex) => {
      roundPairs.forEach(([aId, bId]) => {
        groupMatches.push({
          id: `gm_${generateCode(10)}`,
          groupId: group.id,
          roundIndex,
          aId,
          bId,
          status: "scheduled",
        });
      });
    });
  }

  // 4) KO seeds: placeholders for qualifiers (G1-1, G1-2, ...)
  const qualifiers = [];
  for (const group of groups) {
    for (let pos = 1; pos <= advancePerGroup; pos++) {
      qualifiers.push({
        slotType: "groupPlace",
        groupId: group.id,
        place: pos,
      });
    }
  }
  for (let i = 0; i < includeBestThirds; i++) {
    qualifiers.push({ slotType: "bestThird", index: i + 1 });
  }

  const ko = buildKoBracket({
    bracketSize,
    qualifiers,
    thirdPlaceMatch,
  });

  return {
    kind: "clubmeisterschaft",
    mode: "groupsKo",
    status: "built",
    config: {
      groupCount,
      seedingMode,
      seedCount,
      allowUnevenGroups: allowUneven,
      advancePerGroup,
      includeBestThirds,
      bracketSize,
      thirdPlaceMatch,
    },
    groups,
    groupMatches,
    ko,
  };
}

function makeGroups(players, groupCount, allowUneven) {
  const groups = Array.from({ length: groupCount }, (_, i) => ({
    id: `g${i + 1}`,
    name: `G${i + 1}`,
    playerIds: [],
  }));

  // snake draft distribution
  let dir = 1;
  let idx = 0;
  for (const pl of players) {
    const pid = pl.pid ?? pl.id ?? "";
    if (!pid) continue;
    groups[idx].playerIds.push(pid);
    idx += dir;
    if (idx === groupCount) {
      idx = groupCount - 1;
      dir = -1;
    }
    if (idx === -1) {
      idx = 0;
      dir = 1;
    }
  }

  if (!allowUneven) {
    // optional: enforce equal sizes by trimming (v1: do nothing)
  }

  return groups;
}

/**
 * Circle method RR:
 * returns rounds: Array< Array<[aId,bId]> >
 */
function buildRoundRobin(playerIds) {
  const ids = playerIds.slice();
  if (ids.length < 2) return [];

  const hasBye = ids.length % 2 === 1;
  if (hasBye) ids.push("__bye__");

  const n = ids.length;
  const rounds = n - 1;
  const half = n / 2;

  const arr = ids.slice();

  const out = [];
  for (let r = 0; r < rounds; r++) {
    const pairs = [];
    for (let i = 0; i < half; i++) {
      const a = arr[i];
      const b = arr[n - 1 - i];
      if (a !== "__bye__" && b !== "__bye__") pairs.push([a, b]);
    }
    out.push(pairs);

    // rotate (keep first fixed)
    const fixed = arr[0];
    const rest = arr.slice(1);
    rest.unshift(rest.pop());
    arr.splice(0, arr.length, fixed, ...rest);
  }
  return out;
}

/**
 * Minimal KO bracket builder: creates rounds matches with placeholder entrants.
 * v1: seeding is sequential order of qualifiers -> slots 1..N
 */
function buildKoBracket({ bracketSize, qualifiers, thirdPlaceMatch }) {
  const size = bracketSize;
  const slots = Array.from({ length: size }, (_, i) => ({
    slot: i + 1,
    entrant: qualifiers[i] || { slotType: "tbd" },
  }));

  const rounds = [];
  let currentSize = size;
  let roundIndex = 0;

  while (currentSize >= 2) {
    const matchCount = currentSize / 2;
    const roundMatches = Array.from({ length: matchCount }, (_, m) => ({
      id: `km_${generateCode(10)}`,
      roundIndex,
      roundSize: currentSize,
      matchIndex: m + 1,
      aSlot: m * 2 + 1,
      bSlot: m * 2 + 2,
      status: "scheduled",
      winnerSlot: null,
    }));
    rounds.push({ roundIndex, roundSize: currentSize, matches: roundMatches });
    currentSize = currentSize / 2;
    roundIndex++;
  }

  const third = thirdPlaceMatch
    ? { id: `km_${generateCode(10)}`, type: "thirdPlace", status: "scheduled" }
    : null;

  return { bracketSize: size, slots, rounds, thirdPlace: third };
}
